import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiBookOpen,
  FiZap,
  FiTarget,
  FiClock,
  FiTrendingUp,
  FiPlay,
  FiPause,
  FiRefreshCw,
  FiBarChart,
  FiHome,
} from 'react-icons/fi';
import '../styles/LessonStyles.css';
import GetStartedWith from '../../../../components/GetStartedWith';

const speedReadingTechniques = [
  {
    id: 1,
    technique: 'Eliminate Subvocalization',
    description: 'Stop "hearing" words in your head as you read.',
    methods: [
      'Hum or count while reading',
      'Keep your tongue busy (chew gum)',
      'Focus on visualizing concepts instead of hearing words',
      'Practice reading faster than you can speak',
    ],
    impact: 'Can increase speed from 200-250 WPM to 400+ WPM',
  },
  {
    id: 2,
    technique: 'Minimize Eye Movements',
    description: 'Reduce the number of eye stops (fixations) per line.',
    methods: [
      'Use peripheral vision to capture more words per fixation',
      'Practice reading in vertical lines down the page',
      'Focus on the center of word groups',
      'Avoid regression (looking back at words)',
    ],
    impact: 'Each fixation can capture 3-5 words instead of 1-2',
  },
  {
    id: 3,
    technique: 'Use a Pacer',
    description:
      'Guide your eyes with a finger, pen, or cursor to maintain steady rhythm.',
    methods: [
      'Move finger smoothly across each line',
      'Keep pacer moving at desired speed',
      'Use pacer to prevent regression',
      'Gradually increase pacer speed',
    ],
    impact: 'Maintains consistent speed and prevents wandering',
  },
  {
    id: 4,
    technique: 'Preview and Predict',
    description: 'Scan ahead to anticipate content and context.',
    methods: [
      'Read first and last sentences of paragraphs first',
      'Scan for keywords and topic sentences',
      'Use headings and structure to predict content',
      'Look for transition words and logical connectors',
    ],
    impact: 'Improves comprehension while maintaining speed',
  },
];

const wpmBenchmarks = [
  {
    level: 'Beginner',
    wpm: 200,
    description: 'Average reading speed',
    color: '#EF4444',
  },
  {
    level: 'Intermediate',
    wpm: 300,
    description: 'Above average reader',
    color: '#F59E0B',
  },
  { level: 'Advanced', wpm: 400, description: 'Fast reader', color: '#10B981' },
  {
    level: 'Expert',
    wpm: 500,
    description: 'GRE target speed',
    color: '#6366F1',
  },
  {
    level: 'Professional',
    wpm: 600,
    description: 'Speed reading mastery',
    color: '#8B5CF6',
  },
];

const practiceTexts = [
  {
    id: 1,
    title: 'Warm-up Text (150 words)',
    text: `Speed reading is a valuable skill that can significantly improve your academic and professional performance. The key to effective speed reading lies in breaking old habits that slow you down, such as subvocalization and excessive eye movements.

Most people read at about 200-250 words per minute, but with practice, you can easily reach 400-500 words per minute while maintaining good comprehension. The secret is to train your brain to process groups of words as single units of meaning rather than reading each word individually.

Regular practice is essential for developing speed reading skills. Start with easier texts and gradually work your way up to more complex academic passages. Remember that comprehension should never be sacrificed for speed – the goal is to find the optimal balance between reading speed and understanding.`,
    wordCount: 150,
  },
  {
    id: 2,
    title: 'Practice Text (200 words)',
    text: `The digital revolution has fundamentally altered how we consume and process information in the modern world. With the vast amount of content available online, the ability to read quickly and efficiently has become more important than ever before.

Research shows that speed reading techniques can be learned and mastered by anyone willing to invest the time and effort. The most effective methods focus on expanding visual span, reducing subvocalization, and improving pattern recognition skills.

Professional speed readers often achieve rates of 1000 words per minute or more, though for academic purposes, a target of 500 words per minute is both realistic and highly beneficial. This speed allows you to complete GRE reading comprehension passages in about one minute, leaving ample time for analyzing questions and selecting answers.

The key to successful speed reading implementation is consistent practice combined with active comprehension monitoring. You should regularly test your understanding to ensure that increased speed doesn't compromise your ability to grasp important concepts and details.`,
    wordCount: 200,
  },
  {
    id: 3,
    title: 'Challenge Text (250 words)',
    text: `Neuroscientists have recently discovered fascinating insights into how the human brain processes written language during speed reading activities. Advanced neuroimaging techniques reveal that skilled speed readers exhibit distinct patterns of brain activation compared to traditional readers.

The visual cortex shows enhanced processing efficiency, allowing for rapid pattern recognition of letter combinations and word shapes. Simultaneously, the language processing centers in the left hemisphere demonstrate increased coordination, enabling faster semantic interpretation of textual content.

Interestingly, research indicates that speed reading doesn't simply involve faster processing of the same neural pathways used in normal reading. Instead, the brain develops alternative routes for information processing, bypassing some of the slower mechanisms associated with phonological processing.

This neuroplasticity suggests that speed reading skills can be developed at any age, though younger individuals typically show faster adaptation rates. The most significant improvements occur within the first few weeks of dedicated practice, with continued gains observed over several months.

These findings have important implications for educational methodology and professional development programs. Understanding the neurological basis of speed reading allows for more targeted training approaches that work with the brain's natural learning mechanisms rather than against them.

For students preparing for standardized tests like the GRE, this research validates the effectiveness of speed reading training as a legitimate and scientifically-supported strategy for improving performance.`,
    wordCount: 250,
  },
];

export default function SpeedReadingLesson() {
  const navigate = useNavigate();
  const practiceRef = useRef(null);
  const [selectedTechnique, setSelectedTechnique] = useState(null);
  const [currentText, setCurrentText] = useState(0);
  const [readingTimes, setReadingTimes] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [isReading, setIsReading] = useState(false);
  const [sessionResults, setSessionResults] = useState([]);

  const scrollToPractice = () => {
    practiceRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const startReading = () => {
    setStartTime(Date.now());
    setEndTime(null);
    setIsReading(true);
  };

  const finishReading = () => {
    if (startTime) {
      const end = Date.now();
      setEndTime(end);
      setIsReading(false);

      const timeInMinutes = (end - startTime) / 60000;
      const wpm = Math.round(
        practiceTexts[currentText].wordCount / timeInMinutes
      );

      const result = {
        textId: currentText,
        wpm: wpm,
        time: timeInMinutes,
        timestamp: new Date().toLocaleTimeString(),
      };

      setSessionResults((prev) => [...prev, result]);
    }
  };

  const resetSession = () => {
    setStartTime(null);
    setEndTime(null);
    setIsReading(false);
    setSessionResults([]);
    setCurrentText(0);
  };

  const getCurrentWPM = () => {
    if (!startTime || !endTime) return 0;
    const timeInMinutes = (endTime - startTime) / 60000;
    return Math.round(practiceTexts[currentText].wordCount / timeInMinutes);
  };

  const getWPMLevel = (wpm) => {
    if (wpm >= 600) return wpmBenchmarks[4];
    if (wpm >= 500) return wpmBenchmarks[3];
    if (wpm >= 400) return wpmBenchmarks[2];
    if (wpm >= 300) return wpmBenchmarks[1];
    return wpmBenchmarks[0];
  };

  const renderOverview = () => (
    <div className="lesson-content">
      <h2 className="section-title">Target: 500 Words Per Minute</h2>

      <div className="lesson-intro">
        <p>
          Reading at 500 WPM allows you to complete a typical GRE passage
          (200-250 words) in just 30-40 seconds, leaving 2-3 minutes per
          question for analysis and answering. This is the optimal speed for GRE
          success.
        </p>
      </div>

      {/* WPM Benchmarks */}
      <div style={{ marginBottom: '3rem' }}>
        <h3 style={{ color: 'var(--liwaza-on-surface)', marginBottom: '1rem' }}>
          Reading Speed Benchmarks
        </h3>
        <div className="question-types-grid">
          {wpmBenchmarks.map((benchmark, index) => (
            <div
              key={index}
              style={{
                background: 'var(--liwaza-surface-container)',
                padding: '1rem',
                borderRadius: '8px',
                border: `2px solid ${benchmark.color}`,
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  color: benchmark.color,
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  marginBottom: '0.5rem',
                }}
              >
                {benchmark.wpm} WPM
              </div>
              <div
                style={{
                  color: 'var(--liwaza-on-surface)',
                  fontWeight: 'bold',
                  marginBottom: '0.25rem',
                }}
              >
                {benchmark.level}
              </div>
              <div
                style={{
                  color: 'var(--liwaza-on-surface-variant)',
                  fontSize: '0.9rem',
                }}
              >
                {benchmark.description}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Techniques */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ color: 'var(--liwaza-on-surface)', marginBottom: '1rem' }}>
          Speed Reading Techniques
        </h3>
        <div className="question-types-grid">
          {speedReadingTechniques.map((technique) => (
            <div
              key={technique.id}
              onClick={() => setSelectedTechnique(technique)}
              className={`question-type-card ${selectedTechnique?.id === technique.id ? 'selected' : ''}`}
            >
              <div className="type-header">
                <div className="type-number">
                  <FiZap />
                </div>
                <h4 className="type-title">{technique.technique}</h4>
              </div>
              <p className="type-description">{technique.description}</p>
            </div>
          ))}
        </div>
      </div>

      {selectedTechnique && (
        <div className="detailed-analysis">
          <h3 className="analysis-title">
            <FiTarget />
            {selectedTechnique.technique}
          </h3>

          <div className="analysis-section">
            <h4>How to Practice:</h4>
            <ul className="tips-list">
              {selectedTechnique.methods.map((method, index) => (
                <li key={index}>{method}</li>
              ))}
            </ul>
          </div>

          <div
            style={{
              background: 'var(--liwaza-success-container)',
              color: 'var(--liwaza-on-success-container)',
              padding: '1rem',
              borderRadius: '8px',
            }}
          >
            <strong>Expected Impact:</strong> {selectedTechnique.impact}
          </div>
        </div>
      )}
    </div>
  );

  const renderPractice = () => (
    <div ref={practiceRef} className="practice-section">
      <h2 className="section-title">Speed Reading Practice</h2>

      {/* Session Statistics */}
      {sessionResults.length > 0 && (
        <div className="practice-passage">
          <h3 className="passage-title">
            <FiBarChart />
            Session Progress
          </h3>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '1rem',
              marginBottom: '1rem',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: 'var(--liwaza-primary)',
                }}
              >
                {sessionResults.length}
              </div>
              <div
                style={{
                  color: 'var(--liwaza-on-surface-variant)',
                  fontSize: '0.9rem',
                }}
              >
                Texts Read
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: 'var(--liwaza-primary)',
                }}
              >
                {Math.round(
                  sessionResults.reduce((sum, result) => sum + result.wpm, 0) /
                    sessionResults.length
                )}
              </div>
              <div
                style={{
                  color: 'var(--liwaza-on-surface-variant)',
                  fontSize: '0.9rem',
                }}
              >
                Average WPM
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  color: 'var(--liwaza-primary)',
                }}
              >
                {Math.max(...sessionResults.map((r) => r.wpm))}
              </div>
              <div
                style={{
                  color: 'var(--liwaza-on-surface-variant)',
                  fontSize: '0.9rem',
                }}
              >
                Best WPM
              </div>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                minWidth: 'fit-content',
              }}
            >
              {sessionResults.map((result, index) => (
                <div
                  key={index}
                  style={{
                    background: getWPMLevel(result.wpm).color,
                    color: 'white',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    textAlign: 'center',
                    minWidth: '80px',
                    fontSize: '0.8rem',
                  }}
                >
                  <div style={{ fontWeight: 'bold' }}>{result.wpm}</div>
                  <div>WPM</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Text Selection and Reading Interface - styled with CSS classes */}
      <div className="practice-passage">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
          }}
        >
          <h3 style={{ color: 'var(--liwaza-on-surface)', margin: 0 }}>
            {practiceTexts[currentText].title}
          </h3>

          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            {endTime && (
              <div
                style={{
                  background: getWPMLevel(getCurrentWPM()).color,
                  color: 'white',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                }}
              >
                {getCurrentWPM()} WPM - {getWPMLevel(getCurrentWPM()).level}
              </div>
            )}

            {!isReading && !endTime ? (
              <button
                onClick={startReading}
                style={{
                  background: 'var(--liwaza-primary)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FiPlay /> Start Reading
              </button>
            ) : isReading ? (
              <button
                onClick={finishReading}
                style={{
                  background: 'var(--liwaza-error)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FiPause /> Finish Reading
              </button>
            ) : (
              <button
                onClick={() => {
                  setStartTime(null);
                  setEndTime(null);
                }}
                style={{
                  background: 'var(--liwaza-secondary)',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <FiRefreshCw /> Try Again
              </button>
            )}
          </div>
        </div>

        <div
          style={{
            background: 'var(--liwaza-surface-container-highest)',
            padding: '2rem',
            borderRadius: '8px',
            lineHeight: 1.8,
            color: 'var(--liwaza-on-surface)',
            fontSize: '1.1rem',
          }}
        >
          {practiceTexts[currentText].text
            .split('\n\n')
            .map((paragraph, index) => (
              <p key={index} style={{ marginBottom: '1.5rem' }}>
                {paragraph}
              </p>
            ))}
        </div>

        <div
          style={{
            marginTop: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: 'var(--liwaza-on-surface-variant)',
            fontSize: '0.9rem',
          }}
        >
          <span>Word count: {practiceTexts[currentText].wordCount} words</span>
          {isReading && (
            <span
              style={{ color: 'var(--liwaza-primary)', fontWeight: 'bold' }}
            >
              Reading in progress... <FiClock style={{ display: 'inline' }} />
            </span>
          )}
        </div>
      </div>

      {/* Tips */}
      <div className="lesson-intro">
        <h4
          style={{
            color: 'var(--liwaza-on-primary-container)',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <FiTarget />
          Speed Reading Tips
        </h4>
        <ul
          style={{
            color: 'var(--liwaza-on-primary-container)',
            paddingLeft: '1rem',
            lineHeight: 1.6,
          }}
        >
          <li>Start with easier texts and gradually increase difficulty</li>
          <li>Focus on comprehension first, then gradually increase speed</li>
          <li>Use your finger or a pen as a pacer to maintain rhythm</li>
          <li>Practice for 15-20 minutes daily for best results</li>
          <li>
            Don't aim for perfection - slight comprehension trade-offs are
            normal at higher speeds
          </li>
          <li>
            <strong>Master the passage first:</strong> For complex passages with
            few questions (e.g., 8 mins for 1 passage + 3 questions), spend 4-5
            mins truly understanding the passage, then 30 secs per question.
            This leaves 1 min for review and ensures accuracy.
          </li>
        </ul>
      </div>
    </div>
  );

  return (
    <div className="lesson-container">
      {/* Header */}
      <div className="lesson-header">
        <button
          onClick={() =>
            navigate('/exams/gre/', { state: { activeCategory: 'reading' } })
          }
          className="back-button"
        >
          <FiArrowLeft /> Back to Reading Comprehension
        </button>

        <div className="header-actions">
          <button onClick={scrollToPractice} className="practice-button">
            Practice
          </button>
        </div>
      </div>

      {/* Title */}
      <div className="lesson-title-section">
        <h1 className="lesson-title">
          <FiTrendingUp />
          Lesson 5: Target 500 WPM
        </h1>
        <p className="lesson-subtitle">
          Achieve the optimal reading speed for GRE success. Learn advanced
          techniques and track your progress toward the 500 words-per-minute
          target that will give you a significant time advantage.
        </p>
      </div>

      {/* Content */}
      {renderOverview()}
      {renderPractice()}

      {/* Get Started With Component */}
      <div style={{ margin: '3rem 0 2rem 0' }}>
        <GetStartedWith
          title="Need help with Speed Reading techniques?"
          spaceName="Verbal vault"
          humanCoachTitle="Talk to a GRE coach"
          liwazaAITitle="Ask AI"
          communityTitle="Ask other students"
          showHumanCoach={true}
          showLiwazaAI={true}
          showCommunity={true}
          aifirstmessage="I need help understanding Speed Reading techniques to reach 500 WPM for the GRE"
          aicontext="Reading at 500 WPM allows you to complete a typical GRE passage (200-250 words) in just 30-40 seconds, leaving 2-3 minutes per question for analysis and answering. This is the optimal speed for GRE success."
        />
      </div>

      {/* Navigation */}
      <div className="lesson-navigation">
        <button
          onClick={() =>
            navigate('/exams/gre/reading-comprehension/lesson/chunk-reading')
          }
          className="prev-lesson-button"
        >
          <FiArrowLeft /> Previous: Chunk Reading
        </button>

        <button
          onClick={() =>
            navigate('/exams/gre/reading-comprehension/lesson/master-passage')
          }
          className="next-lesson-button"
        >
          Next: Master the Passage First <FiArrowRight />
        </button>
      </div>
    </div>
  );
}
